import { useAppConfig } from '#/config/AppConfigContext';
import { useFetchFn } from '#/hooks/useFetchFn';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';
import { searchAlgoliaFetchSimilarVolops } from '#/modules/search/algolia/fetch/similarVolops/searchAlgoliaFetchSimilarVolops';
import { useSearchLocationOrEmpty } from '#/modules/search/contexts/SearchContext';
import {
  type SearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { ViewListingPageSidebarSuggestionsContents } from './ViewListingPageSidebarSuggestionsContents';

type Props = {
  listing: ApiVolop;
  searchSavedListingsStore: SearchSavedListingsStore;
};

export function ViewVolopPageSidebarSuggestions({
  listing,
  searchSavedListingsStore,
}: Props) {
  const { appConfig } = useAppConfig();
  const userSearchLocation = useSearchLocationOrEmpty();

  const fetchArgs = [
    listing.name,
    listing.functions,
    userSearchLocation,
    listing.org?.areasOfFocusKeys,
    listing.remoteOptions.locationType,
    appConfig,
  ] as Parameters<typeof searchAlgoliaFetchSimilarVolops>;

  // We don't want to change the results as the user navigates
  // But the search location might load after this functions is called, so make sure to clear it
  const cacheKey = userSearchLocation
    ? 'with-search-location'
    : 'without-search-location';

  const [similarListingsResponse] = useFetchFn({
    fn: searchAlgoliaFetchSimilarVolops,
    args: fetchArgs,
    cacheKey,
  });

  useSearchSavedListingsSyncAlgoliaHits({
    hits: similarListingsResponse?.hits,
    searchSavedListingsStore,
  });

  if (!similarListingsResponse) {
    return null;
  }

  return (
    <ViewListingPageSidebarSuggestionsContents
      similarListingsResponse={similarListingsResponse}
      listing={listing}
      searchSavedListingsStore={searchSavedListingsStore}
    />
  );
}
